import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, filter, take } from 'rxjs';
import { InvitacionEncuestaDialogComponent } from 'src/app/dialogs/encuesta-satisfaccion/invitacion-encuesta-dialog.component';
import { FormularioEncuestaDialogComponent } from 'src/app/dialogs/encuesta-satisfaccion/formulario-encuesta-dialog.component';
import { DatabaseService } from 'src/app/servicios/database/database.service';
import { MessageService } from 'src/app/servicios/message/message.service';
import { MunicipalityTokenService } from 'src/app/servicios/municipality-token/municipality-token.service';

/** Fila pendiente alineada con dbo.EncuestaSatisfaccion */
export interface EncuestaPendiente {
  IdEncuesta: number;
  IdUsuario: number;
  IdAdminEnvio?: number;
  FechaEnvio: string;
  FechaRespuesta?: string | null;
  Pospuesto: boolean;
  VersionEncuesta?: number;
  Respuesta?: string | null;
  /** Alias usados por diálogos legacy del preview */
  idUsuario?: number;
  nombre?: string;
  fechaEnvio?: string;
  pospuesto?: boolean;
  fechaRespuesta?: string | null;
}

@Injectable({ providedIn: 'root' })
export class EncuestaSatisfaccionService {
  private dialog = inject(MatDialog);
  private databaseService = inject(DatabaseService);
  private messageService = inject(MessageService);
  private municipalityTokenService = inject(MunicipalityTokenService);
  private translate = inject(TranslateService);

  private pendingSubject = new BehaviorSubject<EncuestaPendiente | null>(null);
  readonly pending$ = this.pendingSubject.asObservable();

  private checking = false;
  private invitationOpen = false;
  private formOpen = false;
  private bootstrapped = false;

  get pending(): EncuestaPendiente | null {
    return this.pendingSubject.value;
  }

  /**
   * Arranca la comprobación cuando el token de municipio esté listo
   * (necesario para Municipality-Authorization en las APIs).
   */
  bootstrapForLoggedUser() {
    if (this.bootstrapped) return;
    this.bootstrapped = true;

    if (this.municipalityTokenService.isReady()) {
      this.refreshPendiente(true);
      return;
    }

    this.municipalityTokenService
      .getToken$()
      .pipe(
        filter((ctx) => !!ctx),
        take(1),
      )
      .subscribe(() => {
        this.refreshPendiente(true);
      });
  }

  /**
   * Consulta pendiente del usuario autenticado (JWT fviu en backend).
   * @param showInvitationIfNeeded si Pospuesto=0 abre la alerta al destinatario
   */
  refreshPendiente(showInvitationIfNeeded = false) {
    if (this.checking) return;
    this.checking = true;

    this.databaseService.getEncuestaSatisfaccionPendiente().subscribe({
      next: (resp) => {
        this.checking = false;
        const row = this.normalize(resp?.result ?? null);
        this.pendingSubject.next(row);

        const debeAbrir =
          showInvitationIfNeeded && !!row && !row.Pospuesto && !row.FechaRespuesta;

        if (debeAbrir) {
          this.openInvitacion();
        }
      },
      error: () => {
        this.checking = false;
        this.pendingSubject.next(null);
      },
    });
  }

  /** Admin: clasifica usuarios antes de enviar. */
  precheckEnvio(idUsuarios: number[]) {
    return this.databaseService.precheckEncuestaSatisfaccion({ IdUsuarios: idUsuarios });
  }

  /** Admin: crea pendiente. forzar=true reenvía si ya respondió la misma versión. */
  enviarAUsuario(idUsuario: number, forzar = false) {
    return this.databaseService.enviarEncuestaSatisfaccion({
      IdUsuario: idUsuario,
      forzar,
    });
  }

  openInvitacion() {
    const pendiente = this.pendingSubject.value;
    if (!pendiente || pendiente.FechaRespuesta || this.invitationOpen || this.formOpen) {
      return;
    }

    this.invitationOpen = true;
    const ref = this.dialog.open(InvitacionEncuestaDialogComponent, {
      width: '420px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      data: this.toDialogData(pendiente),
    });

    ref.afterClosed().subscribe((action: 'responder' | 'mas_tarde' | undefined) => {
      this.invitationOpen = false;
      if (action === 'responder') {
        this.openFormulario();
      } else if (action === 'mas_tarde') {
        this.marcarPospuesto();
      }
    });
  }

  openFormulario() {
    const pendiente = this.pendingSubject.value;
    if (!pendiente || pendiente.FechaRespuesta || this.formOpen) return;

    this.formOpen = true;
    const ref = this.dialog.open(FormularioEncuestaDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      data: this.toDialogData(pendiente),
    });

    ref.afterClosed().subscribe((result: { enviada?: boolean; respuestas?: Record<string, unknown> } | undefined) => {
      this.formOpen = false;
      if (result?.enviada && result.respuestas) {
        this.enviarRespuestas(result.respuestas);
      }
    });
  }

  private marcarPospuesto() {
    const pendiente = this.pendingSubject.value;
    if (!pendiente) return;

    this.databaseService.posponerEncuestaSatisfaccion({ IdEncuesta: pendiente.IdEncuesta }).subscribe({
      next: (resp) => {
        const row = this.normalize(resp?.result ?? { ...pendiente, Pospuesto: true });
        this.pendingSubject.next(row);
      },
      error: () => {
        this.messageService.SwalMessage(
          'error',
          this.translate.instant('error_posponer_encuesta')
        );
      },
    });
  }

  private enviarRespuestas(respuestas: Record<string, unknown>) {
    const pendiente = this.pendingSubject.value;
    if (!pendiente) return;

    this.databaseService
      .responderEncuestaSatisfaccion({
        IdEncuesta: pendiente.IdEncuesta,
        Respuesta: respuestas,
      })
      .subscribe({
        next: () => {
          this.pendingSubject.next(null);
          this.messageService.SwalMessage(
            'success',
            this.translate.instant('gracias_valoracion')
          );
        },
        error: () => {
          this.messageService.SwalMessage(
            'error',
            this.translate.instant('error_guardar_respuestas')
          );
        },
      });
  }

  private normalize(row: any): EncuestaPendiente | null {
    if (!row) return null;
    const pospuesto = row.Pospuesto === true || row.Pospuesto === 1 || row.Pospuesto === '1';
    return {
      ...row,
      IdEncuesta: Number(row.IdEncuesta),
      IdUsuario: Number(row.IdUsuario),
      Pospuesto: pospuesto,
      FechaRespuesta: row.FechaRespuesta ?? null,
      idUsuario: Number(row.IdUsuario),
      fechaEnvio: row.FechaEnvio,
      pospuesto,
      fechaRespuesta: row.FechaRespuesta ?? null,
    };
  }

  private toDialogData(p: EncuestaPendiente) {
    return {
      ...p,
      idUsuario: p.IdUsuario,
      fechaEnvio: p.FechaEnvio,
      pospuesto: p.Pospuesto,
      fechaRespuesta: p.FechaRespuesta,
    };
  }
}
